import { type Database } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import {
  type DatabaseChangeset,
  type DatabaseDraft,
  getDatabaseDraft,
} from '@databases/utils/database/changesets';
import { applyDatabaseView } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { omit } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import type { DefaultRootState } from '../redux';

export const emptyArr = Object.freeze([]) as never[];

export function selectHasActiveSaveOperation(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): boolean {
  return (state.changesets.activeSaveOperations[contentId] ?? 0) > 0;
}

export const selectDraftedChangeset = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
  },
  string,
  DatabaseChangeset,
  DatabaseChangeset,
  DatabaseChangeset
>(
  (state, { contentId }) => contentId,
  (state, { contentId }) => state.changesets.savingChangeset[contentId] ?? emptyArr,
  (state, { contentId }) => state.changesets.draftedChangesets[contentId] ?? emptyArr,
  (contentId, saving, drafted) => {
    if (!contentId) {
      return emptyArr;
    }

    return [...saving, ...drafted];
  }
)((state, { contentId }) => contentId);

const selectDatabaseDraftByContentIdInternal = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    viewSettings?: DatabaseViewSettings;
    ignoredEntries?: string[];
  },
  Database,
  DatabaseChangeset,
  DatabaseViewSettings | undefined,
  string[] | undefined,
  DefaultRootState,
  DatabaseDraft
>(
  selectDatabaseByContentIdRequired,
  selectDraftedChangeset,
  (state, { viewSettings }) => viewSettings,
  (state, { ignoredEntries }) => ignoredEntries,
  (state) => state,
  (database, changeset, viewSettings, ignoredEntries, state) => {
    const databaseDraft = getDatabaseDraft(database, changeset);
    return viewSettings
      ? applyDatabaseView(database, databaseDraft, viewSettings, state, ignoredEntries)
      : databaseDraft;
  }
)((state, { contentId, viewSettings }) => `${contentId}-${!!viewSettings}`);

export const selectDatabaseDraftByContentId = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    /**
     * If true, deleted fields/entries will be present in the draft (marked as deleted in the "deletedFields" and
     * "deletedEntries" properties.
     * If false, deleted fields/entries will not be present in the draft.
     *
     * This property is not optional as it is a common source of error. Components that receive a fieldId/entryId
     * as a prop and use that to load the field/entry from the database should almost always set this to true,
     * as many parts of the UI keep showing deleted fields/entries until the draft is saved.
     * Setting this to false in combination with using getDatabaseFieldRequired/getDatabaseEntryRequired will usually
     * lead to exceptions and component crashes, so in such cases usually getDatabaseFieldOptional/getDatabaseEntryOptional
     * need to be used.
     */
    includeDeleted: boolean;
    viewSettings?: DatabaseViewSettings;
    ignoredEntries?: string[];
  },
  DatabaseDraft,
  boolean,
  DatabaseDraft
>(
  selectDatabaseDraftByContentIdInternal,
  (state, { includeDeleted }) => includeDeleted,
  (databaseDraft, includeDeleted) => {
    if (includeDeleted) {
      return databaseDraft;
    } else {
      return {
        ...databaseDraft,
        fields: databaseDraft.fields.filter(
          (fieldId) => !databaseDraft.deletedFields.includes(fieldId)
        ),
        fieldsById: omit(databaseDraft.fieldsById, databaseDraft.deletedFields),
        entries: databaseDraft.entries.filter(
          (entryId) => !databaseDraft.deletedEntries.includes(entryId)
        ),
        entriesById: omit(databaseDraft.entriesById, databaseDraft.deletedEntries),
      };
    }
  }
)(
  (state, { contentId, viewSettings, includeDeleted }) =>
    `${contentId}-${!!viewSettings}-${includeDeleted}`
);
